import { useMemo } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import {
  Datagrid,
  DateField,
  DateTimeInput,
  FunctionField,
  List,
  SelectInput,
  TextField,
  TextInput,
  useGetList,
  useRecordContext,
} from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';

/**
 * Audit list — the read surface over the audit floor (D-audit). Read-only and
 * minimal by design: this is the trail being VISIBLE, not a forensics suite.
 *
 * Every create / update / delete the panel performs is recorded automatically by
 * the provider wrapper (../dataProvider/withAudit.ts) and read back here through
 * the normal `getList('audit')` path. Columns: when, actor, action, resource,
 * record, and a compact diff. Filterable by resource and action.
 *
 * ACCESS — super_admin AND compliance (NOT viewer). The audit trail is an oversight /
 * accountability surface: it names individual admins and records the operational
 * context of their actions. Least-privilege says a read-only viewer does not
 * need colleagues' action history — that is an administrative oversight function,
 * not ordinary data. super_admin reads it as the platform owner; compliance reads it
 * as the independent OVERSIGHT role (D-audit-compliance-read): the gate expansion held
 * back from the DSAR build, granted here as a CONSIDERED decision. Compliance gets FULL
 * PARITY (every entry super_admin sees, all actors including super_admin and other
 * compliance officers): oversight that cannot see the most powerful actors is
 * self-defeating, and the redaction floor below already protects sensitive VALUES from
 * every reader. Note this is gated by ROLE, not capability: viewer holds a
 * read-everywhere `*:read` wildcard, so a capability gate would let it in; the
 * explicit `roles` gate is what keeps audit to super_admin and compliance.
 *
 * SECURITY: the entries never carry sensitive values — the wrapper redacts
 * opaque fields (payoutDetails-class) to a marker before they are ever stored,
 * so there is nothing sensitive for this list to leak.
 */

const ACTION_CHIP: Record<
  string,
  { label: string; color: 'success' | 'info' | 'error' }
> = {
  create: { label: 'Create', color: 'success' },
  update: { label: 'Update', color: 'info' },
  delete: { label: 'Delete', color: 'error' },
};

const ActionChip = () => {
  const record = useRecordContext();
  const action = (record?.action as string) ?? '';
  const chip = ACTION_CHIP[action];
  if (!chip) return <span>{action}</span>;
  return <Chip size="small" label={chip.label} color={chip.color} />;
};

// Actor as a two-line cell: email (primary) + role (muted) — same shape as the
// member cells elsewhere, so the panel reads consistently.
const ActorCell = () => {
  const record = useRecordContext();
  return (
    <Box>
      <Typography variant="body2">
        {(record?.actorEmail as string) || (record?.actorId as string) || '—'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {(record?.actorRole as string) ?? '—'}
      </Typography>
    </Box>
  );
};

// Record cell: "<resource> #<id>" so the target is unambiguous at a glance.
const RecordCell = () => {
  const record = useRecordContext();
  return (
    <Typography variant="body2">
      {`${record?.resource as string} #${String(record?.recordId)}`}
    </Typography>
  );
};

// Compact value formatting for the diff: strings as-is, everything else as
// compact JSON, all truncated so a long value can never blow out the row.
const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '∅';
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  return text.length > 40 ? `${text.slice(0, 39)}…` : text;
};

const MAX_FIELDS = 4;

/**
 * Compact diff of the CHANGED fields only. create → "field: after";
 * delete → "field: before"; update → "field: before → after". Caps at a few
 * fields with a "+N more" tail so the cell stays compact.
 */
const DiffCell = () => {
  const record = useRecordContext();
  const before = (record?.before as Record<string, unknown> | null) ?? null;
  const after = (record?.after as Record<string, unknown> | null) ?? null;
  const keys = Array.from(
    new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]),
  );
  if (keys.length === 0) return <span>—</span>;

  const shown = keys.slice(0, MAX_FIELDS);
  const extra = keys.length - shown.length;

  return (
    <Box>
      {shown.map((key) => {
        const hasBefore = before != null && key in before;
        const hasAfter = after != null && key in after;
        return (
          <Typography
            key={key}
            variant="caption"
            component="div"
            sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
          >
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {key}:
            </Box>{' '}
            {hasBefore && (
              <Box
                component="span"
                sx={{
                  textDecoration: hasAfter ? 'line-through' : 'none',
                  color: 'text.secondary',
                }}
              >
                {formatValue(before[key])}
              </Box>
            )}
            {hasBefore && hasAfter ? ' → ' : ''}
            {hasAfter && <Box component="span">{formatValue(after[key])}</Box>}
          </Typography>
        );
      })}
      {extra > 0 && (
        <Typography variant="caption" color="text.secondary" component="div">
          {`+${extra} more`}
        </Typography>
      )}
    </Box>
  );
};

/** The fixed region-context values (the actor's access scope at the time). */
const REGION_CHOICES = [
  { id: 'UK', name: 'UK' },
  { id: 'US', name: 'US' },
  { id: 'GLOBAL', name: 'GLOBAL' },
];

/**
 * The investigative vocabulary, DERIVED FROM REALITY rather than a hardcoded
 * literal that goes stale (D-audit-view): the distinct actors, roles and resources
 * actually present in the trail. One read of the whole log feeds the facet choices;
 * the facets themselves still filter provider-side through `getList`.
 */
const useAuditVocabulary = () => {
  const { data } = useGetList('audit', {
    pagination: { page: 1, perPage: 10_000 },
    sort: { field: 'occurredAt', order: 'DESC' },
  });
  return useMemo(() => {
    const rows = data ?? [];
    const distinct = (sel: (r: (typeof rows)[number]) => unknown): string[] =>
      Array.from(
        new Set(
          rows
            .map(sel)
            .filter((v): v is string => typeof v === 'string' && v.length > 0),
        ),
      ).sort((a, b) => a.localeCompare(b));
    const asChoices = (vals: string[]) => vals.map((v) => ({ id: v, name: v }));
    return {
      actors: asChoices(distinct((r) => r.actorEmail)),
      roles: asChoices(distinct((r) => r.actorRole)),
      resources: asChoices(distinct((r) => r.resource)),
    };
  }, [data]);
};

/** The list itself. Exported for testing without the access wrapper. */
export const AuditListView = () => {
  const vocab = useAuditVocabulary();
  // The audit trail is a GLOBAL administrative record (it spans regions and stores
  // the region on each row as data), so it is NOT region-scoped by the switcher.
  // The investigative facets all drive getList filter params (provider-side); the
  // action facet stays the real three values the floor writes (create/update/delete)
  // with NO derived transition layer (D-audit-view, Q1). `q` matches actor/resource
  // text, the region facet matches the stored access scope by EXACT value (Q2), and
  // From/To drive the occurredAt date-range operator at the provider seam (Q3).
  const auditFilters = [
    <TextInput key="q" source="q" label="Search" alwaysOn resettable />,
    <SelectInput
      key="action"
      source="action"
      label="Action"
      choices={[
        { id: 'create', name: 'Create' },
        { id: 'update', name: 'Update' },
        { id: 'delete', name: 'Delete' },
      ]}
    />,
    <SelectInput
      key="resource"
      source="resource"
      label="Resource"
      choices={vocab.resources}
    />,
    <SelectInput
      key="actorEmail"
      source="actorEmail"
      label="Actor"
      choices={vocab.actors}
    />,
    <SelectInput
      key="actorRole"
      source="actorRole"
      label="Actor role"
      choices={vocab.roles}
    />,
    <TextInput key="recordId" source="recordId" label="Record id" />,
    <SelectInput
      key="region"
      source="region"
      label="Region context"
      choices={REGION_CHOICES}
    />,
    <DateTimeInput key="from" source="occurredAt_gte" label="From" />,
    <DateTimeInput key="to" source="occurredAt_lte" label="To" />,
  ];

  return (
    <List
      resource="audit"
      title="Audit log"
      filters={auditFilters}
      sort={{ field: 'occurredAt', order: 'DESC' }}
      perPage={25}
      exporter={false}
    >
      {/* Read-only: no row navigation and no bulk actions — the trail is immutable. */}
      <Datagrid rowClick={false} bulkActionButtons={false}>
        <DateField source="occurredAt" label="When" showTime />
        <FunctionField label="Actor" render={() => <ActorCell />} />
        <FunctionField
          label="Action"
          sortBy="action"
          render={() => <ActionChip />}
        />
        <TextField source="resource" label="Resource" />
        <FunctionField label="Record" render={() => <RecordCell />} />
        <FunctionField label="Change" render={() => <DiffCell />} />
      </Datagrid>
    </List>
  );
};

/**
 * Gated entry point registered as the `audit` Resource's list. super_admin and compliance
 * (see the access note above): the role gate — not a capability — is what excludes the
 * read-everywhere viewer, and the role list is what admits the compliance oversight reader
 * (D-audit-compliance-read).
 */
export const AuditList = () => (
  <RequireAccess roles={['super_admin', 'compliance']}>
    <AuditListView />
  </RequireAccess>
);
